import { useRBACPermissions } from '@/contexts/useRBACPermissions'
import {
  type SubmitBulkClusterUpdateResponse,
  useApplyBulkUpdate,
  useApproveBulkUpdate,
  useGetBulkClusterUpdates,
  useRejectBulkUpdate,
  useSubmitBulkClusterUpdate,
} from '@/generated/http-clients/v3ApiComponents'
import type { ApprovalResponse } from '@/generated/http-clients/v3ApiSchemas'
import {
  ERROR_TOAST_OPTION,
  SUCCESS_TOAST_OPTION,
  toaster,
} from '@/hooks/useToast/toastUtils'
import get from 'lodash/get'
import {
  type FC,
  type ReactNode,
  createContext,
  useContext,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

interface ClusterUpdatesContextType {
  clusterUpdates: ReturnType<typeof useGetBulkClusterUpdates>
  /**
   * Submits a bulk cluster update request.
   * @param clusterIds - Array of cluster IDs to update.
   * @param templateId - ID of the template to apply.
   * @returns A promise that resolves to the response of the bulk update request.
   */
  submitBulkClusterUpdate: (
    clusterIds: string[],
    templateId: string,
  ) => Promise<SubmitBulkClusterUpdateResponse>
  /**
   * Sets the current page for cluster updates.
   * @param page - The page number to set.
   */
  setPage: (page: number) => void
  approveBulkUpdateTask: (
    taskId: string,
    comment?: string,
  ) => Promise<ApprovalResponse>
  rejectBulkUpdateTask: (
    taskId: string,
    comment?: string,
  ) => Promise<ApprovalResponse>
  applyBulkUpdateTask: (
    taskId: string,
    comment?: string,
  ) => Promise<ApprovalResponse>
}

const ClusterUpdatesContext = createContext<
  ClusterUpdatesContextType | undefined
>(undefined)

export const ClusterUpdatesProvider: FC<{
  children: ReactNode
}> = ({ children }) => {
  const { hasAdminRights } = useRBACPermissions()

  const { t } = useTranslation()

  // Check if user has admin rights before making API calls
  const canAccessClusterUpdates = hasAdminRights()

  const submitBulkClusterUpdateRequest = useSubmitBulkClusterUpdate({
    onSuccess: () => {
      toaster.create({
        ...SUCCESS_TOAST_OPTION,
        title: t('bulkUpdate.feedback.create.success'),
      })
      clusterUpdates.refetch()
    },
    onError: (error) => {
      toaster.create({
        ...ERROR_TOAST_OPTION,
        title: t('bulkUpdate.feedback.create.error'),
        description: get(error, 'message'),
      })
    },
  })

  function submitBulkClusterUpdate(clusterIds: string[], templateId: string) {
    return submitBulkClusterUpdateRequest.mutateAsync({
      body: {
        clusterIds,
        templateId,
      },
    })
  }

  const [page, setPage] = useState(1)
  const clusterUpdates = useGetBulkClusterUpdates(
    {
      queryParams: { page, order: 'desc' },
    },
    {
      // Skip the query if user doesn't have admin rights
      enabled: canAccessClusterUpdates,
      refetchInterval: 20000, // Refetch every 20 seconds
    },
  )

  const approveBulkClusterUpdateRequest = useApproveBulkUpdate({
    onSuccess: () => {
      toaster.create({
        ...SUCCESS_TOAST_OPTION,
        title: t('bulkUpdate.feedback.approve.success'),
      })
      clusterUpdates.refetch()
    },
    onError: (error) => {
      toaster.create({
        ...ERROR_TOAST_OPTION,
        title: t('bulkUpdate.feedback.approve.error'),
        description: get(error, 'message'),
      })
    },
  })

  function approveBulkUpdateTask(
    bulkUpdateId: string,
    comment = '',
  ): Promise<ApprovalResponse> {
    return approveBulkClusterUpdateRequest.mutateAsync({
      pathParams: { bulkUpdateId },
      body: { comment },
    })
  }

  const rejectBulkClusterUpdateRequest = useRejectBulkUpdate({
    onSuccess: () => {
      toaster.create({
        ...SUCCESS_TOAST_OPTION,
        title: t('bulkUpdate.feedback.reject.success'),
      })
      clusterUpdates.refetch()
    },
    onError: (error) => {
      toaster.create({
        ...ERROR_TOAST_OPTION,
        title: t('bulkUpdate.feedback.reject.error'),
        description: get(error, 'message'),
      })
    },
  })

  function rejectBulkUpdateTask(
    bulkUpdateId: string,
    comment = '',
  ): Promise<ApprovalResponse> {
    return rejectBulkClusterUpdateRequest.mutateAsync({
      pathParams: { bulkUpdateId },
      body: { comment },
    })
  }

  const applyBulkClusterUpdateRequest = useApplyBulkUpdate({
    onSuccess: () => {
      toaster.create({
        ...SUCCESS_TOAST_OPTION,
        title: t('bulkUpdate.feedback.apply.success'),
      })
      clusterUpdates.refetch()
    },
    onError: (error) => {
      toaster.create({
        ...ERROR_TOAST_OPTION,
        title: t('bulkUpdate.feedback.apply.error'),
        description: get(error, 'message'),
      })
    },
  })

  function applyBulkUpdateTask(
    bulkUpdateId: string,
    comment = '',
  ): Promise<ApprovalResponse> {
    return applyBulkClusterUpdateRequest.mutateAsync({
      pathParams: { bulkUpdateId },
      body: { comment },
    })
  }

  return (
    <ClusterUpdatesContext.Provider
      value={{
        clusterUpdates,
        submitBulkClusterUpdate,
        setPage,
        approveBulkUpdateTask,
        rejectBulkUpdateTask,
        applyBulkUpdateTask,
      }}
    >
      {children}
    </ClusterUpdatesContext.Provider>
  )
}

export const useClusterUpdates = () => {
  const context = useContext(ClusterUpdatesContext)
  if (!context) {
    throw new Error(
      'useClusterUpdates must be used within an ClusterUpdatesProvider',
    )
  }
  return context
}
